import { SocietyTheme } from "shared/mappers/database/society/theme";

export const hslToHex = (
  theme: SocietyTheme[],
  hueModify?: string,
  saturationModify?: string,
  lightnessModify?: string,
) => {
  const modifyValue = (value: number, modify?: string) => {
    if (typeof modify !== "string") {
      return value;
    }
    if (modify.startsWith("-")) {
      return value - Number(modify.slice(1));
    }
    return value + Number(modify);
  };
  let h = Number(
    theme?.find((item) => item.property === "color-society-hue")?.value,
  );
  let s = Number(
    theme
      ?.find((item) => item.property === "color-society-saturation")
      ?.value.slice(0, -1),
  );
  let l = Number(
    theme
      ?.find((item) => item.property === "color-society-lightness")
      ?.value.slice(0, -1),
  );
  h = modifyValue(h, hueModify);
  s = modifyValue(s, saturationModify);
  l = modifyValue(l, lightnessModify);

  h /= 360;
  s /= 100;
  l /= 100;

  let r = 0,
    g = 0,
    b = 0;

  if (s === 0) {
    r = g = b = l; // achromatic
  } else {
    const hue2rgb = (p: number, q: number, t: number) => {
      if (t < 0) t += 1;
      if (t > 1) t -= 1;
      if (t < 1 / 6) return p + (q - p) * 6 * t;
      if (t < 1 / 2) return q;
      if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
      return p;
    };

    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;

    r = hue2rgb(p, q, h + 1 / 3);
    g = hue2rgb(p, q, h);
    b = hue2rgb(p, q, h - 1 / 3);
  }

  const toHex = (c: number) => {
    const hex = Math.round(c * 255).toString(16);
    return hex.length === 1 ? "0" + hex : hex;
  };

  const hexValue = `#${toHex(r)}${toHex(g)}${toHex(b)}`;

  return hexValue.toUpperCase();
};

/**
 * Convert HSL to RGB
 * Assumes HSL numbers are within standard ranges
 */
const hslToRgb = (hsl: {
  h: number;
  s: number;
  l: number;
}): [number, number, number] => {
  const s = hsl.s / 100;
  const l = hsl.l / 100;

  const c = (1 - Math.abs(2 * l - 1)) * s;
  const hp = hsl.h / 60;
  const x = c * (1 - Math.abs((hp % 2) - 1));
  let rgb = [0, 0, 0];

  if (0 <= hp && hp < 1) rgb = [c, x, 0];
  else if (1 <= hp && hp < 2) rgb = [x, c, 0];
  else if (2 <= hp && hp < 3) rgb = [0, c, x];
  else if (3 <= hp && hp < 4) rgb = [0, x, c];
  else if (4 <= hp && hp < 5) rgb = [x, 0, c];
  else rgb = [c, 0, x];

  const m = l - c / 2;
  return rgb.map((v) => Math.round((v + m) * 255)) as [number, number, number];
};

/**
 * Calculates luminance for an RGB color. Necessary for calculating the best
 * text color for a background.
 */
const rgbToLuminance = (rgb: [number, number, number]): number => {
  const norm = rgb.map((v) => v / 255);
  const lin = norm.map((v) =>
    v <= 0.04045 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4),
  );
  const [r, g, b] = lin;
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
};

/**
 * Calculates luminance contrast. Necessary for calculating the best text color
 * for a background.
 */
const contrast = (L1: number, L2: number): number => {
  const [hi, lo] = L1 >= L2 ? [L1, L2] : [L2, L1];
  return (hi + 0.05) / (lo + 0.05);
};

/**
 * Determines whether to use light or dark text depending on the background
 * and adjusts the background color if necessary.
 */
export const adjustForContrast = (
  base: { h: number; s: number; l: number },
  step = 6,
  target = 4.5,
): { lightText: boolean; bg: string } => {
  const hsl = { ...base };
  let rgb = hslToRgb(hsl);
  let L = rgbToLuminance(rgb);

  const ratioW = contrast(1, L);
  const ratioB = contrast(L, 0);
  const lightText = ratioW >= ratioB;
  let ratio = lightText ? ratioW : ratioB;

  const dir = lightText ? +1 : -1;

  while (ratio < target && hsl.l > 0 && hsl.l < 100) {
    hsl.l = Math.max(0, Math.min(100, hsl.l + dir * step));
    rgb = hslToRgb(hsl);
    L = rgbToLuminance(rgb);
    ratio = lightText ? contrast(1, L) : contrast(L, 0);
  }

  return { lightText, bg: `hsl(${hsl.h}, ${hsl.s}%, ${hsl.l}%)` };
};
